import { Link, useNavigate } from "react-router-dom";

import {
    StyledNavigator,
    IconCart,
    IconUser,
    MenuHeaderComponent,
    IconFileText,
    IconLock,
    IconSearch,
} from "@components";
import {
    PATH_BOOKING,
    PATH_CART,
    PATH_HOME,
    PATH_PROFILE,
    PATH_QUOTES,
    PATH_MY_TRIPS,
    PATH_SEARCH_RESULT,
    PATH_UPDATE_PASSWORD,
    PATH_LOGIN,
    PATH_TERM,
} from "@routes";
import { useAppSelector, selectAuth, selectAccount, resetSearch } from "@redux";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { styled } from "styled-components";
import {
    openPasswordPrompt,
    useHasSetPasswordLocally,
} from "src/Module/Checkout/signupPasswordPrompt";
import {
    breakpoint,
    sessionHelper,
    useWindowDimensions,
    isBottomBarVisible as checkBottomBarVisible,
} from "@utilities";

const getCartUrl = (): string => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlSessionId = urlParams.get("sessionId");
    if (urlSessionId) return `${PATH_CART}?sessionId=${urlSessionId}`;
    if (sessionHelper.hasSession()) return `${PATH_CART}?sessionId=${sessionHelper.getSessionId()}`;
    return PATH_CART;
};

interface IProps {
    $onBackToHome?: () => void;
    $productCount: number;
    $toggleShowMenu?: () => void;
    $isShowMenu?: boolean;
    $isCartLoading?: boolean;
}

export const HeaderNavigatorComponent = (props: IProps) => {
    const { $productCount, $toggleShowMenu, $isShowMenu, $isCartLoading } = props;
    const { jwt } = useAppSelector(selectAuth);
    const { account } = useAppSelector(selectAccount);
    const [isSignedIn, setIsSignedIn] = useState(!_.isNil(jwt) && !_.isNil(account));
    const pathname = window.location.pathname;
    const { width } = useWindowDimensions();

    // Check if bottom bar is visible (mobile only)
    const isBottomBarVisible = checkBottomBarVisible(pathname, width);
    const hasSetPassword = useHasSetPasswordLocally(account?.email);
    const needsPasswordSetup =
        Boolean(account?.is_password_randomly_generated) &&
        account?.sso_type === "email" &&
        !hasSetPassword;

    const listMenuItems = [
        {
            icon: IconUser,
            title: "Profile",
            link: PATH_PROFILE,
        },
        {
            icon: IconFileText,
            title: "Bookings",
            link: PATH_BOOKING,
        },
        {
            icon: IconFileText,
            title: "My Trips",
            link: PATH_MY_TRIPS,
        },
        {
            icon: IconCart,
            title: "Quotes",
            link: PATH_QUOTES,
        },
        {
            icon: IconLock,
            title: needsPasswordSetup ? "Set Password" : "Update Password",
            link: PATH_UPDATE_PASSWORD,
            onClick: needsPasswordSetup ? openPasswordPrompt : undefined,
            alert: needsPasswordSetup,
        },
    ];
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const handleSearchClick = () => {
        dispatch(resetSearch());
        navigate(PATH_HOME);
    };

    useEffect(() => {
        setIsSignedIn(!_.isNil(jwt) && !_.isNil(account));
    }, [jwt, account]);

    return (
        <StyledNavigator {...props}>
            {/* The shop's own homepage, not the brand's marketing site: leaving
                mid-booking dropped the cart and the checkout. */}
            <Link className="backBtn" to={PATH_HOME}>
                <Logo
                    src="/images/logos/Primary_Logo.svg"
                    alt="logo"
                    className="logoItem logoColor"
                />
            </Link>
            <div className="menu">
                {!pathname.includes(PATH_HOME && PATH_SEARCH_RESULT) && (
                    <div onClick={handleSearchClick} className="cartIconWrapper iconWrapper">
                        <IconSearch />
                    </div>
                )}
                <Link
                    to={getCartUrl()}
                    className="cartIconWrapper iconWrapper"
                    style={{
                        pointerEvents: $isCartLoading ? "none" : "auto",
                        opacity: $isCartLoading ? 0.5 : 1,
                    }}
                    data-gtm-id="view-cart"
                >
                    {$isCartLoading ? (
                        <CartLoadingSpinner />
                    ) : (
                        $productCount > 0 && <span className="number">{$productCount}</span>
                    )}
                    <IconCart />
                </Link>
                {!isSignedIn ? (
                    <div className="iconWrapper" onClick={$toggleShowMenu}>
                        <HamburgerIcon>
                            <span></span>
                            <span></span>
                            <span></span>
                        </HamburgerIcon>
                        {$isShowMenu && <OverLay />}
                        {$isShowMenu && <GuestMenu onClose={$toggleShowMenu || undefined} />}
                    </div>
                ) : (
                    !isBottomBarVisible && (
                        <div className="userIconWrapper iconWrapper" onClick={$toggleShowMenu}>
                            <IconUser />
                            {/* Flags a password generated at sign-up and never
                                replaced; the menu behind carries Set Password. */}
                            {needsPasswordSetup && (
                                <span
                                    className="passwordAlert"
                                    title="Set a password for your account"
                                    aria-label="Set a password for your account"
                                >
                                    !
                                </span>
                            )}
                            {$isShowMenu && <OverLay />}
                            {$isShowMenu && <MenuHeaderComponent listMenuItems={listMenuItems} />}
                        </div>
                    )
                )}
            </div>
        </StyledNavigator>
    );
};

const Logo = styled.img`
    /* Sized down from 40px: this is the header every mobile page outside the
       homepage uses, and at the old height the artwork crowded the search, cart
       and account icons sharing the row. */
    max-height: 32px;
    max-width: 100%;
    width: auto;
`;

const OverLay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: transparent;
`;

const HamburgerIcon = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    cursor: pointer;

    span {
        width: 24px;
        height: 2px;
        background-color: #000;
        border-radius: 2px;
    }
`;

const GuestMenu = ({ onClose }: { onClose?: () => void }) => {
    return (
        <GuestMenuWrapper>
            <GuestMenuContainer>
                <Link className="linkItem" to={PATH_LOGIN} onClick={onClose || undefined}>
                    <div className="itemWrapper">
                        <span className="icon">
                            <img src="/images/Log.svg" alt="terms" />
                        </span>
                        <span className="linkText">Login or Signup</span>
                    </div>
                </Link>
                <Link className="linkItem" to={PATH_TERM} onClick={onClose || undefined}>
                    <div className="itemWrapper">
                        <span className="icon">
                            <img src="/images/Frame.svg" alt="terms" />
                        </span>
                        <span className="linkText">Terms and Conditions</span>
                    </div>
                </Link>
                <div className="linkItem">
                    <div className="itemWrapper">
                        <span className="icon">
                            <img src="/images/App.svg" alt="app" />
                        </span>
                        <span className="linkText">
                            Install{" "}
                            <AppLink
                                href="https://play.google.com/store/apps/details?id=com.peterpans.app"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Android
                            </AppLink>
                            {" | "}
                            <AppLink
                                href="https://apps.apple.com/us/app/peterpans-adventure-travel/id6748809454"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                iOS
                            </AppLink>
                            {" App"}
                        </span>
                    </div>
                </div>
            </GuestMenuContainer>
        </GuestMenuWrapper>
    );
};

const GuestMenuWrapper = styled.div`
    position: fixed;
    top: 90px;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: calc(100vh - 90px);
    z-index: 999;
    background-color: #fff;
    overflow-y: auto;

    ${breakpoint.breakOnlyMobile`
        top: 90px;
        height: calc(100vh - 90px);
    `}
`;

const GuestMenuContainer = styled.div`
    width: 255px;
    background-color: #fff;
    position: absolute;
    top: calc(100% + 20px);
    right: 0;
    box-shadow: 4px 4px 45px rgba(0, 0, 0, 0.08);
    border: 1px solid ${(p) => p.theme.colors.gray_5};
    padding: 0 30px 0 16px;

    ${breakpoint.breakOnlyMobile`
        position: relative;
        top: 0;
        width: 100%;
        border: none;
        box-shadow: none;
        padding: 0 30px 30px 16px;
    `}

    .linkItem {
        cursor: pointer;

        .itemWrapper {
            display: flex;
            align-items: center;
            padding: 24px 0;
            border-bottom: 1px solid ${(p) => p.theme.colors.gray_5};
        }

        .icon {
            margin-right: 16px;

            svg {
                width: 20px;

                path {
                    stroke: ${(p) => p.theme.colors.gray_1} !important;
                }
            }

            img {
                width: 20px;
                height: 20px;
            }
        }

        .linkText {
            font-size: 16px;
            font-weight: 500;
            line-height: 21px;
            color: #000;
        }
    }
`;

const AppLink = styled.a`
    color: ${(props) => props.theme.colors.highlight};
    text-decoration: none;
    cursor: pointer;
    
    &:hover {
        text-decoration: underline;
    }

        &:last-child {
            .itemWrapper {
                border-bottom: none;
            }
        }
    }
`;

const CartLoadingSpinner = styled.div`
    position: absolute;
    top: -8px;
    right: -8px;
    width: 16px;
    height: 16px;
    border: 2px solid #f3f3f3;
    border-top: 2px solid ${(props) => props.theme.colors.highlight};
    border-radius: 50%;
    animation: spin 1s linear infinite;

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`;
